import React, { useEffect, useState, useCallback } from 'react';
import { useAxios } from '../../Helpers/AxiosHelper';
import { useTranslation } from 'react-i18next';
import {
    DataTable,
    Column,
    Card,
    SCIcon,
    Dialog,
    ReactCountryFlag,
    ProgressBar,
    DataTableFilterMeta,
    FilterMatchMode,
    ColumnFilterElementTemplateOptions,
    TriStateCheckbox,
    DDCountries,
    DDStakeholderType
} from '../../Common/Imports';
import { ApiResponse, Countries_EXT, Stakeholder_EXT } from '../../Models/Classes';
import { StakeholderType } from '../../Models/Enums';
import StakeholderDetail from './Stakeholders_Detail';
import StakeholderNew from './Stakeholders_New';
import { PermissionHelper, usePageID, usePermissions } from '../../Helpers/Permissions/PermissionContext';
import { ExcelExport } from '../../Common/Utility/ExcelExport';

export const Stakeholders: React.FC = () => {
    const { pageID } = usePageID();
    const P_Stakeholder_Create = { idAspNetRole: usePermissions().userRole, idItem: pageID, idPermission: 1 };
    const P_Stakeholder_Read = { idAspNetRole: usePermissions().userRole, idItem: pageID, idPermission: 2 };
    const P_Stakeholder_Update = { idAspNetRole: usePermissions().userRole, idItem: pageID, idPermission: 3 };
    const P_Stakeholder_Export = { idAspNetRole: usePermissions().userRole, idItem: pageID, idPermission: 5 };

    const { t } = useTranslation();
    const [TableData, SetTableData] = useState<Stakeholder_EXT[]>([]);
    const [TableLoader, SetTableLoader] = useState<boolean>(false);
    const [CreateNewData, setCreateNewData] = useState<boolean>(false);
    const [SelectedStakeholder, setSelectedStakeholder] = useState<Stakeholder_EXT | undefined>();
    const [CountriesData, setCountriesData] = useState<Countries_EXT[] | undefined>();
    const AxiosHelper = useAxios();
    const [TableFilters, setTableFilters] = useState<DataTableFilterMeta>({
        idStakeholder: { value: null, matchMode: FilterMatchMode.EQUALS },
        isActive: { value: null, matchMode: FilterMatchMode.EQUALS },
        name: { value: null, matchMode: FilterMatchMode.CONTAINS },
        vat: { value: null, matchMode: FilterMatchMode.CONTAINS },
        email: { value: null, matchMode: FilterMatchMode.CONTAINS },
        phone: { value: null, matchMode: FilterMatchMode.CONTAINS },
        address: { value: null, matchMode: FilterMatchMode.CONTAINS },
        city: { value: null, matchMode: FilterMatchMode.CONTAINS },
        zip: { value: null, matchMode: FilterMatchMode.CONTAINS },
        region: { value: null, matchMode: FilterMatchMode.CONTAINS },
        idCountry: { value: null, matchMode: FilterMatchMode.EQUALS },
        stakeholderType: { value: null, matchMode: FilterMatchMode.CONTAINS },
    });

    const GetData = useCallback(async () => {
        SetTableLoader(true);
        try {
            const resp = await AxiosHelper.get<ApiResponse<Stakeholder_EXT[]>>(`Stakeholder/GetList`);
            if (resp.data.code === 0) {
                SetTableData(resp.data.data);
            }
            const resp2 = await AxiosHelper.get<ApiResponse<Countries_EXT[]>>(`Countries/GetList`);
            if (resp2.data.code === 0) {
                setCountriesData(resp2.data.data);
            }
        } catch (error) {
            console.error('Error fetching data:', error);
        } finally {
            SetTableLoader(false);
        }
    }, [AxiosHelper]);

    useEffect(() => {
        GetData();
    }, [GetData]);

    const OpenDetailRowTemplate = (rowData: Stakeholder_EXT) => {
        return (
            <PermissionHelper requiredPermission={P_Stakeholder_Update}>
                <SCIcon Name="Edit" OnClick={() => setSelectedStakeholder(rowData)} Pointer />
            </PermissionHelper>);
    };

    const cardHeader = () => {
        return (
            <>
                <div className="col d-flex justify-content-end">
                    <PermissionHelper requiredPermission={P_Stakeholder_Export}>
                        <SCIcon Name="Export" Pointer OnClick={() => ExcelExport(TableData, TableFilters, "EXPORT_Stakeholder")} />
                    </PermissionHelper>
                    <PermissionHelper requiredPermission={P_Stakeholder_Create}>
                        <SCIcon Name="Add" Classes="text-primary" Pointer OnClick={() => setCreateNewData(true)}></SCIcon>
                    </PermissionHelper>
                </div>
            </>
        )
    }

    const updateStatus = async (data: Stakeholder_EXT) => {
        data.isActive = !data.isActive;
        try {
            const resp = await AxiosHelper.post<ApiResponse<Stakeholder_EXT>>(`Stakeholder/UpdateStakeholder/`, data);
            if (resp.data.code == 0) {
                GetData();
            }
        } catch {
            GetData();
        }
    }

    const activeBody = (data: Stakeholder_EXT) => {
        return (
            <PermissionHelper requiredPermission={P_Stakeholder_Update}>
                {!data.isActive ?
                    <SCIcon Name="Lock" Classes="text-danger" Pointer OnClick={() => updateStatus(data)} />
                    :
                    <SCIcon Name="Unlock" Classes="text-success" Pointer OnClick={() => updateStatus(data)} />
                }
            </PermissionHelper>
        );
    }

    const ActiveFilter = (options: ColumnFilterElementTemplateOptions) => {
        return (
            <div className="flex align-items-center">
                <TriStateCheckbox
                    value={options.value}
                    onChange={(e) => {
                        options.filterCallback(e.value); // Pass the selected value (true, false, or null) to filter
                    }}
                />
                <label className="ml-2">Active</label>
            </div>
        );
    };

    const CountryFilterTemplate = (options: ColumnFilterElementTemplateOptions) => {
        return (
            <DDCountries
                value={options.value} // Pass current value to dropdown
                onChange={(e) => options.filterCallback(e)} // Use e directly to update filter with the selected country
            />
        );
    };

    const TypeFilterTemplate = (options: ColumnFilterElementTemplateOptions) => {
        return (

            <DDStakeholderType
                value={options.value} // Pass current value to dropdown
                onSelect={(e) => options.filterCallback(e)} // Use e directly to update filter with the selected country
            />
        );
    };

    return (
        <PermissionHelper requiredPermission={P_Stakeholder_Read}>
            {
                CreateNewData &&
                <Dialog
                    header="New Stakeholder"
                    visible={CreateNewData ? true : false}
                    onHide={() => { setCreateNewData(false); GetData() }}>
                    <StakeholderNew />
                </Dialog>
            }
            {
                SelectedStakeholder &&
                <Dialog
                    draggable={false}
                    header={`Stakeholder (ID:${SelectedStakeholder.idStakeholder})`}
                    visible={SelectedStakeholder ? true : false}
                    maximized
                    onHide={() => { setSelectedStakeholder(undefined); GetData() }}>
                    <StakeholderDetail
                        stakeholderId={SelectedStakeholder.idStakeholder}
                    />
                </Dialog>
            }
            <Card header={cardHeader}>
                {
                    TableLoader ? <ProgressBar mode="indeterminate" style={{ height: '6px' }}></ProgressBar>
                        :
                        <DataTable
                            paginator
                            rows={10}
                            rowsPerPageOptions={[10, 20, 50]}
                            currentPageReportTemplate="Showing {first} to {last} of {totalRecords} records"
                            paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
                            value={TableData}
                            size="small"
                            stripedRows
                            onFilter={(e) => setTableFilters(e.filters)}
                            filters={TableFilters}
                            tableStyle={{ minWidth: '50rem' }}>
                            <Column
                                filter
                                field="idStakeholder"
                                header={t("Stakeholder.List.ID")}
                                bodyClassName="table-cell-nowrap" />
                            <Column
                                filter
                                filterElement={ActiveFilter}
                                field="isActive"
                                body={(e: Stakeholder_EXT) => activeBody(e)}
                                bodyClassName="table-cell-nowrap"
                            />
                            <Column
                                filter
                                field="name"
                                header={t("Stakeholder.List.Name")}
                                bodyClassName="table-cell-nowrap" />
                            <Column
                                filter
                                field="vat"
                                header={t("Stakeholder.List.VAT")}
                                bodyClassName="table-cell-nowrap" />
                            <Column
                                filter
                                field="email"
                                header={t("Stakeholder.List.Email")}
                                bodyClassName="table-cell-nowrap" />
                            <Column
                                filter
                                field="phone"
                                header={t("Stakeholder.List.Phone")}
                                bodyClassName="table-cell-nowrap" />
                            <Column
                                filter
                                field="address"
                                header={t("Stakeholder.List.Address")}
                                bodyClassName="table-cell-nowrap" />
                            <Column
                                filter
                                field="city"
                                header={t("Stakeholder.List.City")}
                                bodyClassName="table-cell-nowrap" />
                            <Column
                                filter
                                field="zip"
                                header={t("Stakeholder.List.ZIP")}
                                bodyClassName="table-cell-nowrap" />
                            <Column
                                filter
                                field="region"
                                header={t("Stakeholder.List.Region")}
                                bodyClassName="table-cell-nowrap" />
                            <Column
                                filter
                                filterElement={CountryFilterTemplate} // Use the template for filtering countries
                                field="idCountry" // Ensure you are filtering by the correct field (idCountry)
                                header={t("Stakeholder.List.Country")} // Update header label to show it's filtering by country
                                body={(e: Stakeholder_EXT) => {
                                    const country = CountriesData?.find(m => m.idCountry === e.idCountry); // Find the country based on idCountry
                                    return (
                                        <>
                                            <ReactCountryFlag
                                                countryCode={country?.isO2 ?? ""} // Use isO2 code for the flag
                                                svg
                                                title={country?.name || ""} // Set the title to display the country name
                                                style={{ marginRight: "10px" }}
                                            />
                                            {country?.name || ""} {/* Display the country name */}
                                        </>
                                    );
                                }}
                                bodyClassName="table-cell-nowrap"
                            />
                            <Column
                                filter
                                field="stakeholderType"
                                filterElement={TypeFilterTemplate}
                                header={t("Stakeholder.List.Type")}
                                body={(e: Stakeholder_EXT) => { return StakeholderType[e.stakeholderType] }}
                                bodyClassName="table-cell-nowrap" />
                            <Column
                                field="isActive"
                                header={t("Stakeholder.List.Status")}
                                body={(e: Stakeholder_EXT) => { const _T = e.isActive ? t("Common.Booleans.Active") : t("Common.Booleans.NotActive"); return _T }}
                                bodyClassName="table-cell-nowrap" />
                            <Column body={OpenDetailRowTemplate}
                                bodyClassName="table-cell-nowrap" />
                        </DataTable>
                }
            </Card >
        </PermissionHelper>
    );
};

export default Stakeholders;
